import { MappedContentPiece as MappedContentPieceInterface } from '../interfaces/MappedContentPiece';
import { ContentPiece as ContentPieceInterface } from '../interfaces/ContentPiece';
import { Tag } from '../interfaces/Tag';
import { Variation } from '../interfaces/Variation';
import Story from '../interfaces/Story';

export default class ContentPieceDTO {
  contentPiece = {} as MappedContentPieceInterface;
  constructor(contentPiece: ContentPieceInterface) {
    this.contentPiece.id = contentPiece.id;
    this.contentPiece.source = contentPiece.attributes.source;
    this.contentPiece.url = contentPiece.attributes.url;
    this.contentPiece.headlines_english =
      contentPiece.attributes.headlines_english;
    this.contentPiece.headlines_original =
      contentPiece.attributes.headlines_original;
    this.contentPiece.raw_content = contentPiece.attributes.raw_content ?? '';
    this.contentPiece.processed_content =
      contentPiece.attributes.processed_content ?? '';
    this.contentPiece.content_type = contentPiece.attributes.content_type ?? '';
    this.contentPiece.author = contentPiece.attributes.author ?? 'unknown';
    this.contentPiece.assets = contentPiece.attributes.assets ?? '';
    this.contentPiece.createdAt = contentPiece.attributes.createdAt ?? '';
    this.contentPiece.updatedAt = contentPiece.attributes.updatedAt ?? '';
    this.contentPiece.language = {
      id: contentPiece.attributes.language.data.id,
      name: contentPiece.attributes.language.data.attributes.name ?? '',
      iso_code: contentPiece.attributes.language.data.attributes.iso_code ?? '',
      isFilterable: contentPiece.attributes.site.data.attributes.isFilterable,
    };
    this.contentPiece.site = {
      id: contentPiece.attributes.site.data.id,
      name: contentPiece.attributes.site.data.attributes.name ?? '',
      url: contentPiece.attributes.site.data.attributes.url ?? '',
      isFilterable: contentPiece.attributes.site.data.attributes.isFilterable,
    };
    this.contentPiece.tags = contentPiece.attributes.tags.data;
    this.contentPiece.cleared_raw_content = contentPiece.attributes.cleared_raw_content;
    this.contentPiece.cleared_html_raw_content = contentPiece.attributes.cleared_html_raw_content;
    this.contentPiece.variation_content_pieces = contentPiece.attributes.variation_content_pieces.data;
    this.contentPiece.facts = contentPiece.attributes.facts ?? '';
    this.contentPiece.type = {
      id: contentPiece.attributes.type.data.id,
      name: contentPiece.attributes.type.data.attributes.name ?? '',
      description: contentPiece.attributes.type.data.attributes.description ?? '',
    };
    this.contentPiece.tips_and_predictions = contentPiece.attributes.tips_and_predictions ?? '';
    this.contentPiece.tv_channels = contentPiece.attributes.tv_channels ?? '';
    this.contentPiece.postCreatedAt = contentPiece.attributes.postCreatedAt;
    this.contentPiece.numberOfWords = contentPiece.attributes.numberOfWords;
    this.contentPiece.stories = contentPiece.attributes.stories.data;
    this.contentPiece.channel_name = contentPiece.attributes.channel_name ?? null;
    this.contentPiece.video_length = contentPiece.attributes.video_length ?? null;
    this.contentPiece.iframe_URL = contentPiece.attributes.iframe_URL ?? null;
  }

  static mapContentPieces(contentPieces: ContentPieceInterface[]): ContentPieceDTO[] {
    return contentPieces.map((contentPiece) => new ContentPieceDTO(contentPiece));
  }

  static getDefaultContentPiece(): ContentPieceDTO {
    const defaultContentPiece: ContentPieceDTO = new ContentPieceDTO({
      id: 0,
      attributes: {
        source: '',
        url: '',
        headlines_english: '',
        headlines_original: '',
        raw_content: '',
        processed_content: '',
        content_type: '',
        author: '',
        assets: '',
        createdAt: new Date(),
        updatedAt: new Date(),
        language: {
          data: {
            id: 0,
            attributes: {
              name: '',
              iso_code: '',
              isFilterable: false
            },
          }
        },
        site: {
          data: {
            id: 0,
            attributes: {
              name: '',
              url: '',
              isFilterable: false
            },
          },
        },
        tags: {
          data: [],
        },
        cleared_raw_content: '',
        cleared_html_raw_content: '',
        variation_content_pieces: {
          data: []
        },
        facts: '',
        tips_and_predictions: '',
        tv_channels: '',
        type: {
          data: {
            id: 0,
            attributes: {
              name: '',
              description: ''
            }
          }
        },
        postCreatedAt: new Date(),
        numberOfWords: 0,
        stories: {
          data: []
        },
        iframe_URL: null,
        video_length: null,
        channel_name: null
      },
    });

    return defaultContentPiece;
  }

  getId(): number {
    return this.contentPiece.id;
  }

  getSource(): string {
    return this.contentPiece.source;
  }

  getUrl(): string {
    return this.contentPiece.url;
  }

  getHeadlinesEnglish(): string {
    return this.contentPiece.headlines_english;
  }

  getHeadlinesOriginal(): string {
    return this.contentPiece.headlines_original;
  }

  getRawContent(): string {
    return this.contentPiece.raw_content;
  }

  getProcessedContent(): string {
    return this.contentPiece.processed_content;
  }

  getContentType(): string {
    return this.contentPiece.content_type;
  }

  getAuthor(): string {
    return this.contentPiece.author;
  }

  getAssets(): string {
    return this.contentPiece.assets;
  }

  getCreatedAt(): Date {
    return this.contentPiece.createdAt;
  }

  getUpdatedAt(): Date {
    return this.contentPiece.updatedAt;
  }

  getLanguageId(): number {
    return this.contentPiece.language.id;
  }

  getLanguageName(): string {
    return this.contentPiece.language.name;
  }

  getLanguageIsoCode(): string {
    return this.contentPiece.language.iso_code;
  }

  getSiteId(): number {
    return this.contentPiece.site.id;
  }

  getSiteName(): string {
    return this.contentPiece.site.name;
  }

  getSiteUrl(): string {
    return this.contentPiece.site.url;
  }

  getTags(): Tag[] {
    return this.contentPiece.tags;
  }

  getClearedRawContent(): string {
    return this.contentPiece.cleared_raw_content;
  }

  getClearedHTMLRawContent(): string {
    return this.contentPiece.cleared_html_raw_content;
  }

  getVariationContentPieces(): Variation[] {
    return this.contentPiece.variation_content_pieces;
  }

  getFacts(): string {
    return this.contentPiece.facts;
  }

  getTvChannels(): string {
    return this.contentPiece.tv_channels;
  }

  getTipsAndPredictions(): string {
    return this.contentPiece.tips_and_predictions;
  }

  getTypeId(): number {
    return this.contentPiece.type.id;
  }

  getTypeName(): string {
    return this.contentPiece.type.name;
  }

  getTypeDescription(): string {
    return this.contentPiece.type.description;
  }

  getPostCreatedAt(): Date {
    return this.contentPiece.postCreatedAt;
  }

  getNumberOfWords(): number {
    return this.contentPiece.numberOfWords;
  }

  getStories(): Story[] {
    return this.contentPiece.stories;
  }

  getChannelName(): string | null {
    return this.contentPiece.channel_name;
  }

  getIframeURL(): string | null {
    return this.contentPiece.iframe_URL;
  }

  getVideoLength(): number | null {
    return this.contentPiece.video_length;
  }
}
